import type {
  LiquidClassDef,
  ModuleStepRef,
  RecipeLabwareDef,
  RecipePayload,
  RuntimeParameterDef,
  StainingStepRef,
} from '@ctcells/contract'
import { Fragment, useState } from 'react'
import { intlLocale, labwareTypeLabel, layoutDetailLabel, t } from '@ctcells/i18n'

import { DataTable, type Column } from '../../atoms/DataTable.tsx'
import { EmptyState } from '../../atoms/EmptyState.tsx'
import { ShortId } from '../../atoms/ShortId.tsx'
import { Card } from '../resourceParts.tsx'
import { readGrid } from '../../figures/labwareRead.ts'
import { RECIPE_LABWARE_ID_COMPACT, withRecipeTablePriority } from './recipeModel.ts'
import type { StepChip } from './recipeForm.ts'

import '../screens.css'
import '../resource.css'

/**
 * 레시피 상세의 **읽기 전용 표들** — Opentrons 프로토콜 상세 탭에 대응한다
 * (`docs/opentrons-alignment.md` §4-2).
 *
 * | Opentrons 탭 | 여기                    | 출처                        |
 * | ------------ | ----------------------- | --------------------------- |
 * | 파라미터     | `RecipeParameterTable`(우리 탭 이름 = **작업 생성 입력**) | `payload.runtime_parameters` |
 * | 랩웨어       | `RecipeLabwareTable`    | `payload.labware`           |
 * | 액체         | `RecipeLiquidClassTable`| `payload.liquid_classes`    |
 * | —            | `RecipeStepTables`      | `centrifuge/assembler/staining` 의 `steps` |
 *
 * 🔴 **전부 읽기다.** 상세의 레시피는 확정·보관본이라 고치는 길이 `PUT /recipes/{id}` 뿐인데
 *    그 경로를 부르는 화면이 없다. 입력 상자로 그리면 「적을 수 있는데 갈 데가 없는 칸」이 된다.
 *    ⚠ 새 레시피의 「값 직접 설정」 편집기(`RecipeEditor.tsx` · 2026-09-29)는 스텝을 짓는다 — 그건
 *      `POST /recipes`(신규)라 이 표와 다른 길이다.
 * 🔴 **없는 열을 만들지 않는다.** Opentrons 파라미터 탭은 `기본값`·`범위` 열을 같이 보이는데
 *    우리 실물 42칸 전수에 그 두 칸이 «없다». 액체 탭의 `색`도 없다(색으로 가르지도 않는다).
 */

/** 값이 없으면 `—`. 🔴 「0」·「없음」으로 접지 않는다 — 모르는 것과 비어 있는 것은 다르다 */
function dash(value: string | number | undefined | null): string {
  if (value === undefined || value === null || value === '') return t('mask.none')
  return typeof value === 'number' ? value.toLocaleString(intlLocale()) : value
}

/**
 * 행 펼침 상태 — **빠진 열의 값이 서는 자리**다(`DataTable.revealHidden` · 서랍 1/3 · eric 2026-09-30).
 * 한 번에 한 행만 펼친다. 같은 행을 다시 누르면 접힌다. 아무것도 안 빠지면 행이 펼침 단추가 되지 않는다.
 */
function useRevealRow() {
  const [expandedKey, setExpandedKey] = useState<string | null>(null)
  return {
    expandedKey,
    onToggleExpand: (key: string) => setExpandedKey((current) => (current === key ? null : key)),
    revealHidden: true,
  } as const
}

/**
 * 작업 생성 때 받을 값 — Opentrons 의 **Run-time Parameters** 자리다.
 *
 * 실측 키 넷(2026-09-21 · 레시피 14건 · 42칸 전수): `name` · `type` · `binds_to` · `mandatory`.
 * ⚠ `binds_to` 는 이 값이 들어갈 자리를 가리키는 **점 경로**다(`reagents.5.lot`) — 식별자라
 *   번역하지 않고 그대로 보인다(에러 코드 원문 노출과 같은 결. eric 확정 2026-09-21).
 */
export function RecipeParameterTable({ params }: { params: readonly RuntimeParameterDef[] }) {
  const reveal = useRevealRow()
  const columns: Column<RuntimeParameterDef>[] = [
    {
      key: 'name',
      header: t('column.name'),
      cell: (row) => <span className="mono">{row.name}</span>,
    },
    { key: 'type', header: t('column.kind'), nowrap: true, cell: (row) => dash(row.type) },
    {
      key: 'mandatory',
      header: t('column.mandatory'),
      nowrap: true,
      /* ⚠ 키가 없으면 「아니오」가 아니라 `—` 다 — 계약이 기본값을 못 박지 않았다 */
      cell: (row) =>
        row.mandatory === undefined ? t('mask.none') : t(row.mandatory ? 'value.yes' : 'value.no'),
    },
    {
      key: 'binds',
      header: t('column.bindsTo'),
      cell: (row) => <span className="mono">{dash(row.binds_to)}</span>,
    },
  ]

  return (
    <Card title={t('card.runtimeParameters')}>
      <DataTable
        caption={t('card.runtimeParameters')}
        columns={withRecipeTablePriority('parameters', columns)}
        density="compact"
        emptyLabel={t('state.empty.runtimeParameters')}
        {...reveal}
        rowKey={(row) => row.name}
        rows={params}
      />
    </Card>
  )
}

/**
 * 레시피 안에 박힌 랩웨어 — 카탈로그(`/labware-definitions`)와 **같은 꼴**이라
 * 격자를 읽는 규칙도 같다(`labwareModel.readGrid` 한 곳).
 *
 * ⚠ 「사진 · 제조사 · 카탈로그 번호」 열은 만들지 않는다 — 우리 정의에 그 칸이 없다.
 */
export function RecipeLabwareTable({ labware }: { labware: readonly RecipeLabwareDef[] }) {
  const reveal = useRevealRow()
  const columns: Column<RecipeLabwareDef>[] = [
    {
      key: 'id',
      header: t('label.defId'),
      /* 🔴 레시피가 랩웨어를 부르는 이름이다(서버 PK 가 아니다) */
      cell: (row) => <span className="mono">{row.id}</span>,
      /*
       * 🔴 **다른 열이 다 빠지고도 넘치면 끝 글자만 남긴다**(서랍 1/3 · 1280 실측 표 336 / 상자 311px).
       *    공백 없는 긴 점 경로(`staining.reagent_bay.ambient.1`)라 열 맞춤 표에서는 접히지 않는다(`table.css`).
       *    끝 조각(`ambient.1` · `cold.2`)이 서로 가르므로 뒤를 남긴다. 원값은 툴팁 · 보조기술 · 행 펼침에 있다.
       */
      compact: {
        priority: RECIPE_LABWARE_ID_COMPACT.priority,
        cell: (row) => <ShortId keep="tail" len={RECIPE_LABWARE_ID_COMPACT.tail} value={row.id} />,
      },
    },
    { key: 'name', header: t('column.name'), cell: (row) => dash(row.display_name) },
    {
      key: 'type',
      header: t('column.kind'),
      nowrap: true,
      cell: (row) => labwareTypeLabel(row.type),
    },
    {
      key: 'layout',
      header: t('column.layoutDetail'),
      nowrap: true,
      cell: (row) => layoutDetailLabel(row.layout_detail),
    },
    {
      key: 'grid',
      header: t('field.grid'),
      nowrap: true,
      cell: (row) => {
        const grid = readGrid(row)
        return grid === null ? t('mask.none') : `${String(grid[0])} × ${String(grid[1])}`
      },
    },
    {
      key: 'wells',
      header: t('column.well'),
      nowrap: true,
      /* ⚠ 웰 수를 격자에서 «곱해» 지어내지 않는다 — 이름 목록이 없으면 모르는 것이다 */
      cell: (row) => dash(row.well_names?.length),
    },
    {
      key: 'location',
      header: t('column.bay'),
      /* 🔴 `positions.yaml` 기준점 ID 다. 한글로 바꿔 보이면 되돌릴 수 없다(§ labwareGeometry) */
      cell: (row) => <span className="mono">{dash(row.location)}</span>,
    },
    {
      key: 'volume',
      header: t('field.defaultVolume'),
      nowrap: true,
      cell: (row) => dash(row.default_well_volume_ul),
    },
  ]

  return (
    <Card title={t('card.recipeLabware')}>
      <DataTable
        caption={t('card.recipeLabware')}
        columns={withRecipeTablePriority('labware', columns)}
        density="compact"
        emptyLabel={t('state.empty.recipeLabware')}
        {...reveal}
        rowKey={(row) => row.id}
        rows={labware}
      />
    </Card>
  )
}

/**
 * 액체 클래스 — Opentrons Protocol Designer 의 **Liquids** 정의에 해당한다.
 *
 * ⚠ 실측은 `{ aqueous_default: { base: 'aqueous' } }` **한 가지뿐**이라 `base` 밖의 키를
 *   지어내지 않는다. 다른 키가 오면 「그 밖의 값」 열에 `키=값` 으로 그대로 편다 —
 *   자유 객체라 계약이 안을 보증하지 않기 때문이다.
 */
export function RecipeLiquidClassTable({
  classes,
}: {
  classes: Readonly<Record<string, LiquidClassDef>>
}) {
  const reveal = useRevealRow()
  const rows = Object.entries(classes).map(([name, def]) => ({ name, def }))

  const columns: Column<{ name: string; def: LiquidClassDef }>[] = [
    {
      key: 'name',
      header: t('column.name'),
      cell: (row) => <span className="mono">{row.name}</span>,
    },
    {
      key: 'base',
      header: t('column.base'),
      nowrap: true,
      cell: (row) => dash(typeof row.def.base === 'string' ? row.def.base : undefined),
    },
    {
      key: 'rest',
      header: t('column.otherValues'),
      cell: (row) => {
        const rest = Object.entries(row.def).filter(([key]) => key !== 'base')
        if (rest.length === 0) return t('mask.none')
        return (
          <span className="mono muted">
            {rest.map(([key, value]) => `${key}=${String(value)}`).join(' · ')}
          </span>
        )
      },
    },
  ]

  return (
    <Card title={t('card.liquidClasses')}>
      <DataTable
        caption={t('card.liquidClasses')}
        columns={withRecipeTablePriority('liquidClasses', columns)}
        density="compact"
        emptyLabel={t('state.empty.liquidClasses')}
        {...reveal}
        rowKey={(row) => row.name}
        rows={rows}
      />
    </Card>
  )
}

/**
 * 모듈별 스텝 표. Opentrons Protocol Designer 의 **step list** 자리다.
 *
 * 🔴 **모듈마다 스텝의 «모양이 다르다».** 원심·어셈블러는 `ModuleStepRef`
 *    (`step_id`·`sequence`·`round`·`note`)이고 염색은 `StainingStepRef`
 *    (`step_id`·`operation`·`target_wells`·`reagent_channel`·`volume_ul`·`wait_sec`)다.
 *    한 표로 합치면 절반이 빈 열이 된다 — 표를 따로 낸다.
 * ⚠ 스텝이 0건인 절은 **표를 내되 빈 문구를 적는다.** 절 자체는 레시피에 실재하기 때문이다.
 *   절(`payload.centrifuge` 등)이 아예 없으면 그 카드를 만들지 않는다.
 */
export function RecipeStepTables({ payload }: { payload: RecipePayload }) {
  const centrifuge = payload.centrifuge
  const assembler = payload.assembler
  const staining = payload.staining

  return (
    <>
      {centrifuge === undefined ? null : (
        <ModuleStepCard steps={centrifuge.steps ?? []} title={t('section.centrifuge')} />
      )}
      {assembler === undefined ? null : (
        <ModuleStepCard steps={assembler.steps ?? []} title={t('section.assembler')} />
      )}
      {staining === undefined ? null : <StainingStepCard steps={staining.steps} />}
    </>
  )
}

function ModuleStepCard({ title, steps }: { title: string; steps: readonly ModuleStepRef[] }) {
  const reveal = useRevealRow()
  const columns: Column<ModuleStepRef>[] = [
    {
      key: 'step',
      header: t('column.step'),
      cell: (row) => <span className="mono">{row.step_id}</span>,
    },
    {
      key: 'sequence',
      header: t('column.sequence'),
      nowrap: true,
      cell: (row) => dash(row.sequence),
    },
    { key: 'round', header: t('field.rounds'), nowrap: true, cell: (row) => dash(row.round) },
    { key: 'note', header: t('column.note'), cell: (row) => dash(row.note) },
  ]

  return (
    <Card title={`${title} · ${t('column.step')}`}>
      <DataTable
        caption={`${title} · ${t('column.step')}`}
        columns={withRecipeTablePriority('moduleSteps', columns)}
        {...reveal}
        density="compact"
        emptyLabel={t('state.empty.recipeSteps')}
        rowKey={(row) => row.step_id}
        rows={steps}
      />
    </Card>
  )
}

/**
 * 셰이킹 한 칸 — 객체를 **읽을 수 있는 한 줄**로 편다(`350 rpm · 300 s`).
 *
 * 🔴 **모르는 키를 버리지 않는다.** 계약이 아는 둘(`rpm`·`duration_sec`)만 단위를 붙여
 *    옮기고 나머지는 `키=값` 원값 그대로 뒤에 붙인다 — 「액체 클래스」 표의 「그 밖의 값」
 *    열과 같은 규칙이다. 버리면 화면이 받은 것을 조용히 없애는 셈이다.
 * 🔴 **빠진 `rpm` 을 숫자로 지어내지 않는다.** 계약은 「미지정 = 장비 하한」이라 했는데
 *    그 하한은 capability 에 있고 이 화면에 없다 — 모르는 값은 안 적는다.
 */
function shakeText(shake: StainingStepRef['shake']): string | undefined {
  if (shake === undefined) return undefined
  const parts: string[] = []
  if (typeof shake.rpm === 'number') parts.push(`${shake.rpm.toLocaleString(intlLocale())} rpm`)
  if (typeof shake.duration_sec === 'number')
    parts.push(`${shake.duration_sec.toLocaleString(intlLocale())} s`)
  for (const [key, value] of Object.entries(shake)) {
    if (key === 'rpm' || key === 'duration_sec') continue
    parts.push(`${key}=${String(value)}`)
  }
  return parts.length === 0 ? undefined : parts.join(' · ')
}

/** 불리언 한 칸. 🔴 키가 «없으면» 「아니오」가 아니라 `—` 다 — 계약이 기본값을 못 박지 않았다 */
function yesNo(value: boolean | undefined): string {
  return value === undefined ? t('mask.none') : t(value ? 'value.yes' : 'value.no')
}

function StainingStepCard({ steps }: { steps: readonly StainingStepRef[] }) {
  const reveal = useRevealRow()
  const columns: Column<StainingStepRef>[] = [
    {
      key: 'step',
      header: t('column.step'),
      /*
       * ⚠ `s09_ab_wash_pbst_c3_remove` 처럼 길고 **공백이 없는** 값이라 일반 줄바꿈이
       *   일어날 자리가 없다. 열이 늘어난 지금 이게 없으면 표가 카드를 넘는다
       *   (시약 표의 `자리` 열에서 실제로 났던 것과 같은 성질 — `RecipeSections` 주석).
       */
      wrapAnywhere: true,
      cell: (row) => <span className="mono">{row.step_id}</span>,
    },
    {
      key: 'operation',
      header: t('column.operation'),
      nowrap: true,
      /* ⚠ 실측 동작값을 한글 사전으로 덮지 않는다 — 어휘 목록을 계약이 못 박지 않았다 */
      cell: (row) => <span className="mono">{dash(row.operation)}</span>,
    },
    {
      /*
       * 🔴 어휘를 한글로 덮지 않는다 — `per_sample` 은 「환자 그룹이 바뀔 때 교체」라는
       *    도메인 규칙이고 두 글자로 옮기면 그 뜻이 사라진다(`StainingStepRef.tip_policy`).
       */
      key: 'tip',
      header: t('column.tipPolicy'),
      nowrap: true,
      cell: (row) => <span className="mono">{dash(row.tip_policy)}</span>,
    },
    {
      key: 'channel',
      header: t('column.channel'),
      nowrap: true,
      cell: (row) => dash(row.reagent_channel),
    },
    {
      key: 'volume',
      header: t('column.totalVolume'),
      nowrap: true,
      cell: (row) => dash(row.volume_ul),
    },
    {
      /* `payload.liquid_classes` 의 이름을 가리킨다 — 「시약」 탭의 액체 클래스 표와 같은 값 */
      key: 'liquid',
      header: t('column.liquidClass'),
      wrapAnywhere: true,
      cell: (row) => <span className="mono">{dash(row.liquid_class)}</span>,
    },
    {
      key: 'towaste',
      header: t('column.toWaste'),
      nowrap: true,
      cell: (row) => yesNo(row.to_waste),
    },
    {
      key: 'waste',
      header: t('column.wasteStream'),
      nowrap: true,
      cell: (row) => <span className="mono">{dash(row.waste_stream)}</span>,
    },
    {
      key: 'shake',
      header: t('column.shake'),
      nowrap: true,
      cell: (row) => <span className="mono">{dash(shakeText(row.shake))}</span>,
    },
    {
      key: 'wells',
      header: t('column.well'),
      cell: (row) => {
        const wells = row.target_wells ?? []
        return wells.length === 0 ? (
          t('mask.none')
        ) : (
          <span className="mono">{wells.join(' · ')}</span>
        )
      },
    },
    /*
     * 🔴 **`대기 (s)` 열을 걷었다**(2026-09-22). 실물 레시피 16건 · 염색 스텝 1,136개
     *    «전수»에 `wait_sec` 키가 한 번도 없다 — 열 전체가 `—` 였다(CLAUDE.md
     *    「`—` 로 채운 열을 깔아두지 마라」). 타입에는 남아 있다(계약에 있다).
     * ⚠ 되살리려면 그 값을 «주는» 레시피를 먼저 확인해라. 계약에 있다는 것만으로는 부족하다.
     */
  ]

  return (
    <Card title={`${t('section.staining')} · ${t('column.step')}`}>
      <DataTable
        caption={`${t('section.staining')} · ${t('column.step')}`}
        columns={withRecipeTablePriority('stainingSteps', columns)}
        {...reveal}
        density="compact"
        emptyLabel={t('state.empty.recipeSteps')}
        rowKey={(row) => row.step_id}
        rows={steps}
      />
    </Card>
  )
}

/**
 * 「단계」 칩 줄 — 디자인팀 시안 `docs/design/CTcells-console.html` 의 `.steps3`(레시피 목록 「단계」 열 · 2026-10-12 적용).
 * 종전 `원심 57 · 어셈블 101 · 염색 71` 한 줄 글자를 **칩 셋**(옅은 면 + 옅은 테두리 · 이름은 보조 글자 · 수는 굵게)으로 가른다.
 * 🔴 칩마다 줄바꿈 금지 덩어리다 — 칩 «사이»에서만 꺾인다(한 칩 안의 「어셈블 / 101」 꺾임 금지 · 2026-09-29 규칙 그대로).
 * ⚠ 글자는 `원심 57 어셈블 101 염색 71` 로 이어진다(보조기술 · 복사). 구분점 `·` 은 칩 모양이 대신한다.
 */
export function StepCountChips({ chips }: { chips: readonly StepChip[] }) {
  return (
    <span className="stepchips">
      {chips.map((chip, index) => (
        <Fragment key={chip.key}>
          {/* 칩 사이 공백 — 화면에는 안 그려지고(flex) 글자로 이을 때(복사 · 보조기술) 단어를 가른다 */}
          {index === 0 ? null : ' '}
          <span className="stepchips__chip">
            {chip.label} <b className="stepchips__n">{chip.count.toLocaleString(intlLocale())}</b>
          </span>
        </Fragment>
      ))}
    </span>
  )
}

/** 탭 안이 통째로 비었을 때. 🔴 「없다」가 아니라 «이 레시피가 그 절을 두지 않는다»다 */
export function RecipeTabEmpty({ label }: { label: string }) {
  return <EmptyState label={label} />
}
